import type { PlanColumn, PlanDefinition, PlanDraft, PublishedWeek } from "./types.js";
import { draftFromPublished } from "./week.js";

/**
 * DESIGN.md §16.5 Start and §50.1: how a week with no draft gets one. The portal and the Worker
 * (`POST /team/plans/:monday/start`) both start a week here, so either makes the same week.
 */

export interface StartSources {
  drafts: string[];
  published: string[];
  loadDraft: (monday: string) => Promise<PlanDraft | null>;
  loadPublished: (monday: string) => Promise<PublishedWeek | null>;
}

// The package has no DOM or Node types; every runtime it runs in (browsers, Workers, Node) has this.
const clone = <T>(value: T): T => (globalThis as unknown as { structuredClone<V>(value: V): V }).structuredClone(value);

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function newColumnID(taken: Iterable<string> = []): string {
  const used = new Set(taken);
  for (;;) {
    const id = `c${Math.random().toString(36).slice(2, 8)}`;
    if (!used.has(id)) return id;
  }
}

export function blankDraft(monday: string, columns: string[] = ["Tous"], title = ""): PlanDraft {
  const ids: string[] = [];
  const cols: PlanColumn[] = columns.map((name) => {
    const id = newColumnID(ids);
    ids.push(id);
    return { id, name };
  });
  return {
    week: monday,
    title,
    columns: cols,
    days: Array.from({ length: 7 }, (_, i) => ({ date: addDays(monday, i), location: "", cells: {} })),
    sessions: [],
    notes: [],
  };
}

/** `Week 6` → `Week 7`; anything else stays as written. */
export function nextTitle(title: string): string {
  const match = /^(.*?)(\d+)(\s*)$/.exec(title);
  if (!match) return title;
  return `${match[1]}${Number(match[2]) + 1}${match[3]}`;
}

/** Last week again, a week on: the coach's usual start (§17.1). */
export function copyWeek(source: PlanDraft, monday: string): PlanDraft {
  const copy = clone(source);
  return {
    week: monday,
    title: nextTitle(source.title),
    columns: copy.columns,
    days: copy.days.slice(0, 7).map((day, i) => ({ ...day, date: addDays(monday, i) })),
    sessions: copy.sessions,
    notes: copy.notes,
    basedOn: source.week,
  };
}

/** A draft from the server, made safe to edit: seven days, cells only for real columns. */
export function normalizeDraft(draft: PlanDraft, monday: string): PlanDraft {
  const ids = new Set(draft.columns.map((c) => c.id));
  const days = Array.from({ length: 7 }, (_, i) => {
    const day = draft.days[i];
    const cells: Record<string, string[]> = {};
    for (const [id, chips] of Object.entries(day?.cells ?? {})) if (ids.has(id) && chips.length) cells[id] = [...chips];
    return { date: addDays(monday, i), location: day?.location ?? "", cells };
  });
  const out: PlanDraft = {
    week: monday,
    title: draft.title ?? "",
    columns: draft.columns.map((c) => (c.target ? { ...c, target: clone(c.target) } : { ...c })),
    days,
    sessions: (draft.sessions ?? []).map((s) => {
      const definition: PlanDefinition = { label: s.label, lines: [...s.lines] };
      // A definition written with the form keeps its rows (DESIGN §25.1), or the next save would drop them.
      if (s.rows) definition.rows = clone(s.rows);
      if (s.rowsVersion !== undefined) definition.rowsVersion = s.rowsVersion;
      if (s.writtenWith) definition.writtenWith = s.writtenWith;
      if (s.logs) definition.logs = clone(s.logs);
      // The saved reading carries the coach's Logs picks (DESIGN §61.2), or the next save would drop them.
      if (s.reading) definition.reading = clone(s.reading);
      return definition;
    }),
    notes: (draft.notes ?? []).map((n) => ({ title: n.title, lines: [...n.lines] })),
  };
  if (draft.basedOn) out.basedOn = draft.basedOn;
  return out;
}

/** `draftFromPublished`, or null when it throws or gives back something that is not a week. */
function readPublished(week: PublishedWeek, monday: string): PlanDraft | null {
  try {
    const value = draftFromPublished(week, monday) as unknown;
    if (typeof value !== "object" || value === null) return null;
    const draft = value as PlanDraft;
    return Array.isArray(draft.columns) && Array.isArray(draft.days) && draft.days.length === 7 ? draft : null;
  } catch {
    return null;
  }
}

const latest = (weeks: string[]) => weeks.reduce<string | null>((m, w) => (!m || w > m ? w : m), null);

/**
 * The week before `monday` as a draft to copy (§17.6 Start): the latest earlier week, written here
 * or published from the phone; a week that has both is copied from its draft. A week the phone
 * published has no title, so it takes the one counted on from the latest titled draft before it.
 */
export async function sourceBefore(monday: string, sources: StartSources): Promise<PlanDraft | null> {
  const drafts = sources.drafts.filter((w) => w < monday);
  const week = latest([...drafts, ...sources.published.filter((w) => w < monday)]);
  if (!week) return null;
  if (drafts.includes(week)) {
    const draft = await sources.loadDraft(week);
    if (draft) return normalizeDraft(draft, week);
  }
  const published = await sources.loadPublished(week);
  if (published) {
    const read = readPublished(published, week);
    const draft = read ? normalizeDraft(read, week) : published.columns?.length ? blankDraft(week, published.columns) : null;
    if (draft) {
      if (!draft.title) draft.title = await titleCountedTo(week, drafts.filter((w) => w < week), sources);
      return draft;
    }
  }
  const earlier = { ...sources, drafts: drafts.filter((w) => w < week), published: sources.published.filter((w) => w < week) };
  return sourceBefore(week, earlier);
}

/** "Week 4" three weeks before `monday` makes "Week 7"; "" when no earlier draft is titled with a number. */
async function titleCountedTo(monday: string, drafts: string[], sources: StartSources): Promise<string> {
  for (const week of [...drafts].sort().reverse()) {
    const title = (await sources.loadDraft(week))?.title ?? "";
    const match = /^(.*?)(\d+)(\s*)$/.exec(title);
    if (!match) continue;
    const steps = Math.round((Date.parse(monday) - Date.parse(week)) / (7 * 86_400_000));
    return `${match[1]}${Number(match[2]) + steps}${match[3]}`;
  }
  return "";
}

export async function startWeek(monday: string, sources: StartSources): Promise<PlanDraft> {
  const source = await sourceBefore(monday, sources);
  if (!source) return blankDraft(monday);
  if (source.days.every((d) => Object.keys(d.cells).length === 0) && source.sessions.length === 0) {
    // A published week that could not be read back: its columns, nothing else, and no basedOn to diff against.
    return { ...blankDraft(monday), columns: clone(source.columns) };
  }
  return copyWeek(source, monday);
}

/**
 * A week published from the phone, with no draft, made into one to edit: its own columns, cells and
 * sessions as read back, titled after the draft before it when there is one.
 */
export async function startFromPublished(monday: string, week: PublishedWeek, sources: StartSources): Promise<PlanDraft | null> {
  const read = readPublished(week, monday);
  if (!read) return null;
  const draft = normalizeDraft(read, monday);
  if (!draft.title) draft.title = await titleCountedTo(monday, sources.drafts.filter((w) => w < monday), sources);
  return draft;
}
